export interface SplitTickTextResult {
    lines: string[];
    wasTruncated: boolean;
}

const splitLongWordWithHyphen = (
    word: string,
    maxCharsPerLine: number,
): string[] => {
    // Keep short words untouched; long tokens are chunked with trailing hyphens.
    if (word.length <= maxCharsPerLine || maxCharsPerLine <= 1) {
        return [word];
    }

    const chunks: string[] = [];
    const chunkSize = Math.max(1, maxCharsPerLine - 1);
    let start = 0;

    while (start < word.length) {
        const end = Math.min(start + chunkSize, word.length);
        const chunk = word.slice(start, end);
        const hasMore = end < word.length;
        chunks.push(hasMore ? `${chunk}-` : chunk);
        start = end;
    }

    return chunks;
};

const canAppendTokenToLine = (
    currentLine: string,
    token: string,
    maxCharsPerLine: number,
): boolean => {
    if (currentLine.length === 0) {
        return token.length <= maxCharsPerLine;
    }
    return `${currentLine} ${token}`.length <= maxCharsPerLine;
};

const buildTruncatedResult = (
    lines: string[],
    maxLines: number,
    maxCharsPerLine: number,
): SplitTickTextResult => {
    const truncated = lines.slice(0, maxLines);
    const last = truncated[maxLines - 1] ?? '';
    const clipped = last.slice(0, Math.max(1, maxCharsPerLine - 3)).trimEnd();
    truncated[maxLines - 1] = `${clipped}...`;
    return { lines: truncated, wasTruncated: true };
};

export const splitTickTextIntoLines = (
    input: string,
    maxLines: number,
    maxCharsPerLine: number,
): SplitTickTextResult => {
    const words = input.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return { lines: [''], wasTruncated: false };
    }

    const lines: string[] = [];
    let currentLine = '';
    let didTruncate = false;

    // Pre-split long tokens so regular wrapping logic can stay simple.
    const tokens = words.flatMap((word) =>
        splitLongWordWithHyphen(word, maxCharsPerLine),
    );

    for (const token of tokens) {
        if (canAppendTokenToLine(currentLine, token, maxCharsPerLine)) {
            currentLine = currentLine ? `${currentLine} ${token}` : token;
            continue;
        }

        if (currentLine) {
            lines.push(currentLine);
            if (lines.length >= maxLines) {
                // We already filled the allowed line budget.
                didTruncate = true;
                break;
            }
        }

        currentLine = token;
    }

    if (currentLine && lines.length < maxLines) {
        lines.push(currentLine);
    }

    if (!didTruncate) {
        return { lines, wasTruncated: false };
    }

    // Final safety clamp when content exceeds maxLines: trim last line and append ellipsis.
    return buildTruncatedResult(lines, maxLines, maxCharsPerLine);
};
